import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useEffect, useState, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { RemoveFormFieldButton } from '@/components/Buttons/RemoveFormFieldButton/RemoveFormFieldButton';
import { BasicChip } from '@/components/Chips/BasicChip';
import { FormAutocompleteField } from '@/components/Form/FormAutocompleteField/FormAutocompleteField';
// import { LabeledRadio } from '@/components/LabeledRadio/LabeledRadio';
import { ProfileCard } from '@/components/QuickSearch/ProfileCard/ProfileCard';
import { EntityType, Hco } from '@/core/graphql/generated/types';
import { useDcrFields } from '@/features/DataChangeRequest/_constants/useDcrFields';
import { useDcrContext } from '@/features/DataChangeRequest/_context/DcrContext';
import { DcrFormController } from '@/features/DataChangeRequest/DcrFormController/DcrFormController';
import type { HcoSection } from '@/pages/MyDCRsPage/ViewDCRModal/_hooks/useHcoFieldNames';
import { getAffiliationCaption } from '@/utils/affiliations/getAffiliationCaption';
import {
	RelationshipTypeContext,
	useRelationshipTypeCodesForContext
} from '@/utils/affiliations/useRelationshipTypeLabel';
import { formatAddress } from '@/utils/formatters/formatAddress';
import type { RequirePair } from '@/utils/typescript/RequirePair';

import { useDirtyFieldCheck } from '../../../_hooks/useDirtyFieldCheck';
import {
	useHcoAffiliationsSearch,
	HcoOption
} from '../../../_hooks/useHcoAffiliationsSearch';
import type { AffiliationsSectionValuesIn } from '../../../_schemas/AffiliationsSection.validation';

import { affiliationsSectionCardStyles as sx } from './AffiliationsSectionsCard.styles';

type AffiliationData = AffiliationsSectionValuesIn['parenthcos'][number];

const POTENTIAL_DIRTY_FIELDS: (keyof AffiliationData)[] = [
	'hcoNameCda_V',
	'jobTitle_V',
	'typeOfRelationship_VId',
	'classOfWork_V',
	'isPrimaryRelationship_V',
	'hcoStatus'
];

type AffiliationsFieldName = 'parenthcos';

type Props = {
	index: number;
	onSetPrimary: () => void;
	isLastItem: boolean;
	isExpanded: boolean;
	originalData?: AffiliationData;
	isNewCard: boolean;
	entityType?: EntityType;
	fieldName?: AffiliationsFieldName;
	sectionId?: HcoSection;
	includeRelationshipTypeIds?: string[];
} & RequirePair<'canBeRemoved', 'onRemove', () => void>;

export const AffiliationsSectionCard = ({
	index,
	onRemove,
	// onSetPrimary,
	canBeRemoved,
	isLastItem,
	isExpanded,
	originalData,
	isNewCard,
	entityType,
	fieldName = 'parenthcos',
	sectionId = 'affiliations',
	includeRelationshipTypeIds
}: Props) => {
	const { t } = useTranslation();
	const { affiliationFields } = useDcrFields();
	const {
		control,
		setValue,
		formState: { errors }
	} = useFormContext<AffiliationsSectionValuesIn>();

	const {
		entityCountryId,
		picklists: { relationshipTypeOptions }
	} = useDcrContext();

	const relationshipTypeContext: RelationshipTypeContext =
		entityType === EntityType.Hcp ? 'hcp' : 'hcoParent';

	const validRelationshipTypeCodes = useRelationshipTypeCodesForContext(
		relationshipTypeContext
	);

	const [expanded, setExpanded] = useState(isExpanded);

	const values = useWatch({
		control,
		name: `${fieldName}.${index}`
	});

	const headerTitle = (values?.hcoNameCda_V as string) || t('New Affiliation');

	const {
		options: hcoSearchResults,
		handleChange: onHcoNameSearch,
		loading
	} = useHcoAffiliationsSearch({ entityCountryId });

	const checkIsDirty = useDirtyFieldCheck<AffiliationData>(
		isNewCard,
		originalData,
		['isPrimaryRelationship_V']
	);

	const hasAnyFieldDirty = useMemo(() => {
		if (!values) return false;
		return POTENTIAL_DIRTY_FIELDS.some(key => checkIsDirty(key, values[key]));
	}, [values, checkIsDirty]);

	const affiliationCaption = useMemo(
		() =>
			getAffiliationCaption({
				hcoType: values?.hcoType as string,
				location: values?.location as string
			}),
		[values]
	);

	const handleHcoSelect = (item: Hco | null) => {
		if (item) {
			const a = item.addresses?.[0];
			const address = formatAddress(a);

			setValue(`${fieldName}.${index}.parentHcoVid`, item.vid_V, {
				shouldDirty: true
			});
			setValue(`${fieldName}.${index}.hcoNameCda_V`, item.hcoNameCda_V ?? '', {
				shouldValidate: true,
				shouldDirty: true
			});
			setValue(`${fieldName}.${index}.hcoType`, item?.hcoType?.name ?? '', {
				shouldDirty: true
			});
			setValue(`${fieldName}.${index}.location`, address, {
				shouldDirty: true
			});
			setValue(`${fieldName}.${index}.hcoStatus`, item.statusCda_V ?? '', {
				shouldDirty: true
			});
		}
	};

	const isRelationshipTypeLocked = includeRelationshipTypeIds?.length === 1;

	const lockedRelationshipTypeName = useMemo(
		() =>
			isRelationshipTypeLocked
				? relationshipTypeOptions?.find(
						opt => opt.id === includeRelationshipTypeIds[0]
					)?.name
				: undefined,
		[
			relationshipTypeOptions,
			isRelationshipTypeLocked,
			includeRelationshipTypeIds
		]
	);

	// Note (Ben): bad/legacy data may have a relationship type outside the locked
	// value (e.g. an HCP-only code on an HCO row). Only fill blank (new) rows
	// with the locked type — never overwrite an existing mismatched value.
	useEffect(() => {
		if (lockedRelationshipTypeName && !values?.typeOfRelationship_VId) {
			setValue(
				`${fieldName}.${index}.typeOfRelationship_VId`,
				lockedRelationshipTypeName
			);
		}
	}, [lockedRelationshipTypeName, fieldName, index, setValue, values]);

	const hasMismatchedLockedType =
		isRelationshipTypeLocked &&
		!!values?.typeOfRelationship_VId &&
		values.typeOfRelationship_VId !== lockedRelationshipTypeName;

	const showRelationshipTypeField =
		!isRelationshipTypeLocked || hasMismatchedLockedType;

	const filteredRelationshipTypeOptions = useMemo(() => {
		const contextOptions = (relationshipTypeOptions ?? []).filter(opt =>
			validRelationshipTypeCodes.includes(opt.id)
		);
		const scopedOptions = includeRelationshipTypeIds?.length
			? contextOptions.filter(opt =>
					includeRelationshipTypeIds.includes(opt.id)
				)
			: contextOptions;

		const currentValue = values?.typeOfRelationship_VId;
		const hasCurrentValue = scopedOptions.some(
			opt => opt.name === currentValue
		);
		if (currentValue && !hasCurrentValue) {
			const currentOption = relationshipTypeOptions?.find(
				opt => opt.name === currentValue
			);
			if (currentOption) return [...scopedOptions, currentOption];
		}

		return scopedOptions;
	}, [
		relationshipTypeOptions,
		validRelationshipTypeCodes,
		includeRelationshipTypeIds,
		values?.typeOfRelationship_VId
	]);

	const options = useMemo(
		() =>
			isNewCard
				? hcoSearchResults
				: ([
						{
							id: values?.parentHcoVid ?? '',
							name: values?.hcoNameCda_V ?? ''
						}
					] as HcoOption[]),
		[hcoSearchResults, values, isNewCard]
	);

	useEffect(() => {
		setExpanded(isExpanded);
	}, [isExpanded]);

	const hasError = useMemo(() => {
		return !!errors[fieldName]?.[index];
	}, [errors, fieldName, index]);

	return (
		<Box sx={sx.root}>
			<Box
				sx={() => sx.header(isLastItem)}
				onClick={() => setExpanded(!expanded)}
			>
				<Box sx={sx.headerContent(hasAnyFieldDirty, hasError)}>
					<ExpandMoreIcon sx={sx.expandIcon(expanded)} />
					<Typography sx={sx.headerText}>{headerTitle}</Typography>
					<Box sx={sx.primaryToggleWrap}>
						<DcrFormController
							name={`${fieldName}.${index}.isPrimaryRelationship_V`}
							control={control}
							filterable={false}
							render={({ field }) =>
								!field.value ? (
									<></>
								) : (
									<BasicChip label={t('Primary')} variant='primary' />
								)
							}
						/>
					</Box>
				</Box>

				{canBeRemoved && (
					<Box sx={sx.removeButton}>
						<RemoveFormFieldButton type='button' onClick={onRemove} />
					</Box>
				)}

				<DcrFormController
					name={`${fieldName}.${index}.hcoStatus`}
					control={control}
					filterable={false}
					render={({ field }) => (
						<FormControlLabel
							label={t('Active')}
							sx={{
								marginRight: 0.5
							}}
							control={
								<Switch
									checked={field.value === 'Active'}
									onClick={e => e.stopPropagation()}
									onChange={(_, checked) => {
										const newValue = checked ? 'Active' : 'Inactive';
										field.onChange(newValue);
									}}
								/>
							}
						/>
					)}
				/>
			</Box>

			<Collapse in={expanded}>
				<Box sx={sx.content(expanded)}>
					<Box sx={sx.gridContainer}>
						<DcrFormController
							name={`${fieldName}.${index}.hcoNameCda_V`}
							section={sectionId}
							control={control}
							render={({ field, fieldState }) => (
								<FormAutocompleteField<HcoOption>
									isReadOnly={!isNewCard}
									isRequired
									isAsync
									id={field.name}
									name={field.name}
									section={sectionId}
									label={affiliationFields.hcoNameCda_V.label}
									placeholder={t('Search for HCO')}
									options={options}
									loading={loading}
									onChange={value => {
										onHcoNameSearch(value);
									}}
									handleOnDropdownItemSelect={handleHcoSelect}
									defaultValue={values?.hcoNameCda_V}
									indicateError={!!fieldState.error}
									isDirty={checkIsDirty('hcoNameCda_V', field.value)}
									canBeRemoved={false}
									canAddNewItem={false}
									renderOption={option => (
										<ProfileCard
											profile={option}
											withIcon={false}
											withFullAddress
											customSx={{
												name: {
													fontWeight: 700
												}
											}}
										/>
									)}
									caption={affiliationCaption}
								/>
							)}
						/>
						{/* Note (Filip): As requested by Jonas on 23.01.2026, the logic for setting a primary entity should stay, but we are currently displaying a "Primary" chip */}
						{/* <Box sx={sx.primaryToggleWrap}>
                <DcrFormController
                  name={`parenthcos.${index}.isPrimaryRelationship_V`}
                  control={control}
                  render={({ field, fieldState }) => (
                    <LabeledRadio
                      label={t('Primary')}
                      disabled={true} // As of 22.01.2026 - request by Jonas
                      checked={!!field.value}
                      onChange={onSetPrimary}
                      indicateError={!!fieldState.error}
                      isDirty={checkIsDirty(
                        'isPrimaryRelationship_V',
                        field.value
                      )}
                    />
                  )}
                />
              </Box> */}

						{/* Note: job_title hidden pre-launch — not accepted by Network API */}
						{/* {entityType === EntityType.Hcp && (
							<DcrFormController
								name={`parenthcos.${index}.jobTitle_V`}
								section={section}
								control={control}
								render={({ field, fieldState }) => (
									<FormTextField
										id={field.name}
										name={field.name}
										label={affiliationFields.jobTitle_V.label}
										defaultValue={(field.value as string) ?? ''}
										onChange={field.onChange}
										indicateError={!!fieldState.error}
										isDirty={checkIsDirty('jobTitle_V', field.value)}
									/>
								)}
							/>
						)} */}

						{showRelationshipTypeField && (
							<DcrFormController
								name={`${fieldName}.${index}.typeOfRelationship_VId`}
								section={sectionId}
								control={control}
								render={({ field, fieldState }) => (
									<FormAutocompleteField
										id={field.name}
										name={field.name}
										section={sectionId}
										label={affiliationFields.typeOfRelationship_VId.label}
										options={filteredRelationshipTypeOptions}
										handleOnDropdownItemSelect={item => {
											const option = filteredRelationshipTypeOptions.find(
												opt => opt.id === item?.id
											);
											field.onChange(option?.name ?? item?.id ?? '');
										}}
										defaultValue={(field.value as string) ?? ''}
										indicateError={!!fieldState.error}
										isDirty={checkIsDirty(
											'typeOfRelationship_VId',
											field.value
										)}
										canBeRemoved={false}
										canAddNewItem={false}
									/>
								)}
							/>
						)}
					</Box>
				</Box>
			</Collapse>
		</Box>
	);
};
